import React, { useEffect, useState } from 'react';
import {
  fetchSecurityQuestions,
  fetchUsrSecurityQuestions,
  createUsrSecurityQuestions,
  patchUsrSecurityQuestions
} from './Profile';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';

const OrgSecuritySection = () => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [userQuestions, setUserQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState(Array(5).fill({ question_id: '', answer: '' }));
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState({ question_id: '', answer: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchSecurityQuestions().then(setAllQuestions);
    fetchUsrSecurityQuestions().then(setUserQuestions);
  }, []);

  const handleDropdownChange = (index, field, value) => {
    const updated = [...selectedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedQuestions(updated);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    const isValid = selectedQuestions.every(q => q.question_id && q.answer.trim() !== '');
    const uniqueQuestionIds = new Set(selectedQuestions.map(q => q.question_id));

    if (!isValid) {
      setFormError('Please complete all questions and answers.');
      setIsSaving(false);
      return;
    }
    if (uniqueQuestionIds.size < selectedQuestions.length) {
      setFormError('Each question must be unique.');
      setIsSaving(false);
      return;
    }

    try {
      await createUsrSecurityQuestions(selectedQuestions.map(q => ({
        question_id: q.question_id,
        answer_hash: q.answer.trim()
      })));
      setShowModal(false);
      setFormError('');
      fetchUsrSecurityQuestions().then(setUserQuestions);
    } catch {
      setFormError('Failed to save. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateQuestion = async (index) => {
    if (!editValue.question_id || !editValue.answer.trim()) {
      setFormError('Both question and answer are required.');
      return;
    }
    setIsUpdating(true);
    setFormError('');

    try {
      await patchUsrSecurityQuestions({
        id: userQuestions[index].id,
        question_id: editValue.question_id,
        answer_hash: editValue.answer.trim(),
      });
      setEditingIndex(null);
      fetchUsrSecurityQuestions().then(setUserQuestions);
    } catch {
      setFormError('Could not update. Try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="w-100 h-100 d-flex flex-column">
      <h5 className="text-center mb-3">Security Questions</h5>

      {/* Setup Button at Top */}
      {userQuestions.length === 0 && (
        <div className="text-center mb-3">
          <Button variant="outline-primary" onClick={() => setShowModal(true)}>
            Setup Security Questions
          </Button>
        </div>
      )}

      {formError && <Alert variant="danger">{formError}</Alert>}

      {/* User Questions List */}
      {userQuestions.length > 0 && (
        <ul className="list-group flex-grow-1 overflow-auto">
          {userQuestions.map((uq, index) => (
            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
              {editingIndex === index ? (
                <div className="w-100">
                  <Form.Select
                    className="mb-2"
                    value={editValue.question_id}
                    onChange={(e) => setEditValue({ ...editValue, question_id: e.target.value })}
                  >
                    <option value="">-- Select a question --</option>
                    {allQuestions.map(qItem => (
                      <option key={qItem.id} value={qItem.id}>{qItem.question_text}</option>
                    ))}
                  </Form.Select>
                  <Form.Control
                    type="text"
                    placeholder="Answer"
                    className="mb-2"
                    value={editValue.answer}
                    onChange={(e) => setEditValue({ ...editValue, answer: e.target.value })}
                  />
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="success" onClick={() => handleUpdateQuestion(index)} disabled={isUpdating}>
                      {isUpdating ? 'Saving...' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => setEditingIndex(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <span>{index + 1}. {uq.question_text}</span>
                  <Button size="sm" variant="link" onClick={() => { setEditingIndex(index); setEditValue({ question_id: uq.question_id, answer: '' }); }}>Edit</Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered scrollable size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Setup Security Questions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          {selectedQuestions.map((q, idx) => (
            <div className="mb-3" key={idx}>
              <Form.Group>
                <Form.Label>Question {idx + 1}</Form.Label>
                <Form.Select
                  value={q.question_id}
                  onChange={(e) => handleDropdownChange(idx, 'question_id', e.target.value)}
                >
                  <option value="">-- Select a question --</option>
                  {allQuestions.map(qItem => (
                    <option key={qItem.id} value={qItem.id} disabled={selectedQuestions.some((sq, i) => i !== idx && sq.question_id === qItem.id)}>
                      {qItem.question_text}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Control
                  type="text"
                  placeholder="Enter your answer"
                  value={q.answer}
                  onChange={(e) => handleDropdownChange(idx, 'answer', e.target.value)}
                />
              </Form.Group>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowModal(false)} disabled={isSaving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgSecuritySection;
